import { useAiToolkitContext } from '@app/(darkHeader)/ai/_shared/_context/AiToolkitContext';
import { type JSX, useCallback } from 'react';

import { ConfirmationModal } from '@videoblocks/design-system';

import { pluralize } from '../../../../../../utils/utils';
import { getCreditCost } from '../../_context/getCreditCost';
import { AiToolkitCategory, AI_TOOLKIT_CATEGORIES } from '../../_context/types';

type ConfirmCreditSpendModalProps = {
    onConfirm: () => void | Promise<void>;
};

const getModalTitle = (category: AiToolkitCategory, calculatedCost: number): string => {
    switch (category) {
        case AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION:
            return `Generate this video for ${calculatedCost.toLocaleString()} ${pluralize(calculatedCost, 'credit')}?`;
        case AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION:
            return `Generate this image for ${calculatedCost.toLocaleString()} ${pluralize(calculatedCost, 'credit')}?`;
        case AI_TOOLKIT_CATEGORIES.VIDEO_EDIT:
            // TODO: Add video edit modal title
            return '';
        case AI_TOOLKIT_CATEGORIES.VOICEOVER:
            return '';
    }
};

const getModalContent = (category: AiToolkitCategory, remainingCredits: number): string => {
    switch (category) {
        case AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION:
            return `You'll have ${remainingCredits.toLocaleString()} ${pluralize(remainingCredits, 'credit')} remaining.`;
        case AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION:
            return `You'll have ${remainingCredits.toLocaleString()} ${pluralize(remainingCredits, 'credit')} remaining.`;
        case AI_TOOLKIT_CATEGORIES.VIDEO_EDIT:
            // TODO: Add video edit modal content
            return '';
        case AI_TOOLKIT_CATEGORIES.VOICEOVER:
            return '';
    }
};

const getModalConfirmLabel = (category: AiToolkitCategory): string => {
    switch (category) {
        case AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION:
            return 'Generate Video';
        case AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION:
            return 'Generate Image';
        case AI_TOOLKIT_CATEGORIES.VIDEO_EDIT:
            // TODO: Add video edit modal confirm label
            return '';
        case AI_TOOLKIT_CATEGORIES.VOICEOVER:
            return '';
    }
};

export const ConfirmCreditSpendModal = ({ onConfirm }: ConfirmCreditSpendModalProps): JSX.Element => {
    const { confirmationModal, dispatchConfirmationModal, credits, formStates } = useAiToolkitContext();
    const { type, category = AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION } = confirmationModal ?? {};
    const isOpen = type === 'creditConfirmation' && confirmationModal?.isOpen === true;
    const calculatedCost = getCreditCost(category, formStates);
    const remainingCredits = credits - calculatedCost;

    const onCancel = useCallback((): void => {
        dispatchConfirmationModal({ type: 'CLOSE_MODAL' });
    }, [dispatchConfirmationModal]);

    const handleConfirm = useCallback((): void => {
        void onConfirm();
    }, [onConfirm]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            title={getModalTitle(category, calculatedCost)}
            content={getModalContent(category, remainingCredits)}
            confirmLabel={getModalConfirmLabel(category)}
            cancelLabel="Cancel"
            onConfirm={handleConfirm}
            onCancel={onCancel}
        />
    );
};
